<template>
  <div class="mapbox-container">
    <div ref="map" class="mapbox"></div>
    <slot>
      <!-- POPUP CONTENT -->
    </slot>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import { resizable } from 'views/mixins/index';

export default {
  name: 'Mapbox',

  mixins: [resizable],

  data() {
    return {
      map: null,
      popup: null,
    };
  },

  activated() {
    this.onResize();
  },

  mounted() {
    // init map
    mapboxgl.accessToken =
      'pk.eyJ1Ijoic2VudGlhbmNlc29sdXRpb25zIiwiYSI6ImNrZmRwOG9jbTFydDEydHBwcWltdHJ3OTAifQ.D5hnqwdC7hoHjwc98RpeCA';
    this.map = new mapboxgl.Map({
      container: this.$refs.map,
      // style: 'mapbox://styles/sentiancesolutions/cjeyd69oi17q32rqmh4paowcx',
      style: 'mapbox://styles/sentiancesolutions/cjeyddpc21a8i2rqmn57tqv4o',
      zoom: 0.5,
    });

    // async load handler
    this.map.on('load', () => {
      this.initMap();
      this.$emit('load', this.map);
    });
  },

  methods: {
    initMap() {
      // controls
      this.map.addControl(
        new mapboxgl.NavigationControl({ showCompass: false })
      );
      this.map.addControl(new mapboxgl.FullscreenControl());

      // TODO is it worth abstracting map attributes into config, methods, or objects, i.e. addGeoLayer() or this.points = new MapPointsLayer(map, points)?

      // popup
      const markerHeight = 24;
      const markerRadius = 12;
      this.popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: {
          top: [0, markerHeight],
          bottom: [0, -markerHeight],
          left: [markerHeight, 0],
          right: [-markerHeight, 0],
          'top-left': [markerRadius, markerRadius],
          'top-right': [-markerRadius, markerRadius],
          'bottom-left': [markerRadius, -markerRadius],
          'bottom-right': [-markerRadius, -markerRadius],
        },
      });

      this.onResize();
    },

    showPopup(html, location) {
      this.popup.addTo(this.map).setHTML(html);
      if (location) {
        this.popup.setLngLat([location.longitude, location.latitude]);
      }
    },

    hidePopup() {
      if (this.popup) {
        this.popup.remove();
      }
    },

    fitBounds(bounds) {
      this.map.fitBounds(bounds, {
        padding: 20,
      });
    },

    goTo(location, zoom = 12) {
      this.map.setCenter({ lng: location.longitude, lat: location.latitude });
      this.map.setZoom(zoom);
    },

    flyTo(location, zoom = 12, duration = 1000) {
      this.map.flyTo({
        center: [location.longitude, location.latitude],
        zoom,
        duration,
      });
    },

    zoomTo(level, duration = 0) {
      this.map.zoom(level, { duration });
    },

    onResize() {
      if (this.map._loaded) {
        this.map.resize();
      }
    },
  },
};
</script>

<style lang="scss">
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';
@import '~scss/utilities/mixins.scss';

.mapbox-container {
  height: 100%;
  width: 100%;
}

.mapbox {
  font-family: $family-sans-serif;
  width: 100%;
  height: 100%;
  border-radius: 2px;
  background: black;

  .marker {
    display: flex;
    cursor: pointer;
  }

  .mapboxgl-ctrl-logo,
  .mapboxgl-ctrl-attrib,
  .mapboxgl-ctrl-compass {
    display: none;
  }

  .mapboxgl-ctrl-top-right {
    .mapboxgl-ctrl {
      margin-top: 0.5rem;
      margin-right: 0.5rem;
    }
  }

  .mapboxgl-ctrl-group {
    border-radius: 2px;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.24),
      0 4px 12px 0 rgba(0, 0, 0, 0.06);

    > button {
      width: 1.5rem;
      height: 1.5rem;
      border-bottom-color: rgba(#979797, 0.12);
      transition: $transition;

      &:before {
        @include material-icons();
        position: relative;
        font-size: 1.125rem;
        color: rgba($black, 0.87);
        opacity: 0.5;
      }
    }
  }

  .mapboxgl-ctrl-icon {
    &.mapboxgl-ctrl-zoom-in {
      background-image: none;

      &::before {
        content: 'add';
        top: 2px;
      }
    }

    &.mapboxgl-ctrl-zoom-out {
      background-image: none;

      &::before {
        content: 'remove';
        top: 2px;
      }
    }

    &.mapboxgl-ctrl-fullscreen {
      background-image: none;

      &::before {
        content: 'fullscreen';
        top: 1px;
      }
    }

    &.mapboxgl-ctrl-shrink {
      background-image: none;

      &::before {
        content: 'fullscreen_exit';
        top: 1px;
      }
    }
  }

  .mapboxgl-popup-tip {
    display: none;
  }

  .mapboxgl-popup-content {
    background-color: transparent;
    border-radius: 0;
    box-shadow: none;
    padding: 0;
    @include diffused-drop-shadow(0 2px 40px 0 rgba(0, 0, 0, 0.24));
  }
}
</style>
